import React, { useState, useEffect } from 'react';
import { MemberUser, LanguageCode, DonationRecord, EventItem } from '../types';
import { translations } from '../i18n/translations';
import { DonationHistoryTable } from '../components/DonationHistoryTable';
import {
  CreditCard,
  HeartHandshake,
  Calendar,
  FileText,
  MessageSquare,
  Bell,
  QrCode,
  Download,
  Send,
  Edit,
  ShieldCheck,
  Lock,
  Radio,
  Fingerprint,
  ScanFace,
  UserCheck,
  Newspaper,
  Layers,
  BookOpen,
  GraduationCap,
  Book,
  Landmark,
  Tv,
  Video,
  BarChart3,
  Target,
  Receipt,
  Printer,
  Plus,
  ImageIcon,
  FolderPlus,
  Archive,
  FileCheck,
  FileCode,
  FileSpreadsheet,
  FileEdit,
  PhoneCall,
  MapPin,
  Share2,
  ChevronRight,
  ChevronLeft,
  Copy,
  Check,
  Loader2,
  Home,
  CheckCircle2,
  Clock,
  Sliders,
  Users,
  Search,
  Mail,
  Database,
  Globe,
  Tag,
  Hash,
  PenTool,
  LayoutDashboard,
  LogOut,
  Info,
  Building,
  UserPlus,
  Play,
  Award
} from 'lucide-react';

interface MemberPanelProps {
  currentLang: LanguageCode;
  user: MemberUser;
  donations: DonationRecord[];
  events: EventItem[];
  onLogout: () => void;
  onShowToast?: (message: string, type?: 'success' | 'info' | 'warning', title?: string) => void;
}

export const MemberPanel: React.FC<MemberPanelProps> = ({
  currentLang,
  user,
  donations,
  events,
  onLogout,
  onShowToast,
}) => {
  const t = translations[currentLang];

  // Primary Tab / Module State
  const [activeTab, setActiveTab] = useState<string>('dashboard');

  // Navigation View Level: 'main_grid' | 'sub_grid' | 'action'
  const [viewLevel, setViewLevel] = useState<'main_grid' | 'sub_grid' | 'action'>('main_grid');

  useEffect(() => {
    const handleReset = () => {
      setActiveTab('dashboard');
      setViewLevel('main_grid');
    };
    const handleChangeTab = (e: CustomEvent<string>) => {
      if (e.detail) {
        setActiveTab(e.detail);
        setViewLevel('sub_grid');
      }
    };
    window.addEventListener('reset-member-dashboard', handleReset);
    window.addEventListener('change-member-tab', handleChangeTab as EventListener);
    return () => {
      window.removeEventListener('reset-member-dashboard', handleReset);
      window.removeEventListener('change-member-tab', handleChangeTab as EventListener);
    };
  }, []);

  // Module Sub-Tab States
  const [profileSubTab, setProfileSubTab] = useState<'card' | 'edit' | 'biometric' | 'security'>('card');
  const [duesSubTab, setDuesSubTab] = useState<'pending' | 'history' | 'receipt' | 'auto_pay'>('pending');
  const [eventsSubTab, setEventsSubTab] = useState<'upcoming' | 'tickets' | 'history' | 'calendar'>('upcoming');
  const [newsSubTab, setNewsSubTab] = useState<'announcements' | 'all' | 'press'>('announcements');
  const [activitiesSubTab, setActivitiesSubTab] = useState<'list' | 'reports' | 'docs'>('list');
  const [cultureSubTab, setCultureSubTab] = useState<'lessons' | 'dictionary' | 'history' | 'materials'>('lessons');
  const [donationsSubTab, setDonationsSubTab] = useState<'new' | 'history' | 'campaigns' | 'receipts'>('new');
  const [gallerySubTab, setGallerySubTab] = useState<'photos' | 'videos' | 'archive'>('photos');
  const [documentsSubTab, setDocumentsSubTab] = useState<'membership_cert' | 'charter' | 'forms' | 'downloads'>('membership_cert');
  const [chatSubTab, setChatSubTab] = useState<'live' | 'board' | 'notifications'>('live');
  const [liveSubTab, setLiveSubTab] = useState<'watch' | 'archive'>('watch');
  const [contactSubTab, setContactSubTab] = useState<'info' | 'whatsapp' | 'map'>('info');

  // Profile form state
  const [phoneVal, setPhoneVal] = useState(user.phone || '');
  const [addressVal, setAddressVal] = useState(user.address || '');
  const [cityVal, setCityVal] = useState(user.city || '');
  const [countryVal, setCountryVal] = useState(user.country || '');

  // Online Donation / Deposit State
  const [userDonationsList, setUserDonationsList] = useState<DonationRecord[]>(donations);
  const [selectedDonationAmount, setSelectedDonationAmount] = useState<number>(50);
  const [customDonationAmount, setCustomDonationAmount] = useState<string>('');
  const [donationCategory, setDonationCategory] = useState<string>('Newroz 2026 Kültür Etkinlik Fonu');
  const [paymentMethod, setPaymentMethod] = useState<'creditCard' | 'bankTransfer' | 'paypal'>('creditCard');
  const [donorNameInput, setDonorNameInput] = useState<string>(user.fullName);
  const [donorNoteInput, setDonorNoteInput] = useState<string>('');
  const [cardNumberInput, setCardNumberInput] = useState<string>('4543 8812 9940 3312');
  const [cardExpiryInput, setCardExpiryInput] = useState<string>('12/28');
  const [cardCvcInput, setCardCvcInput] = useState<string>('321');
  const [isProcessingDonation, setIsProcessingDonation] = useState<boolean>(false);

  const finalDonationAmount = customDonationAmount ? parseFloat(customDonationAmount) || 0 : selectedDonationAmount;

  const handleProcessDonation = (e: React.FormEvent) => {
    e.preventDefault();
    if (finalDonationAmount <= 0) {
      if (onShowToast) onShowToast('Lütfen geçerli bir bağış miktarı giriniz.', 'warning', 'Bağış İşlemi');
      return;
    }

    setIsProcessingDonation(true);
    setTimeout(() => {
      const newDonation: DonationRecord = {
        id: 'DON-' + Date.now().toString().slice(-6),
        donorName: donorNameInput || user.fullName,
        donorEmail: user.email || 'uye@kurdernegi.org',
        amount: finalDonationAmount,
        currency: 'EUR',
        campaign: donationCategory,
        date: new Date().toISOString().split('T')[0],
        paymentMethod: paymentMethod === 'creditCard' ? 'Sanal POS (Kredi Kartı)' : paymentMethod === 'bankTransfer' ? 'SEPA / IBAN Transferi' : 'PayPal',
        receiptNo: 'REC-' + Math.floor(100000 + Math.random() * 900000),
      };

      setUserDonationsList([newDonation, ...userDonationsList]);
      setIsProcessingDonation(false);
      setDonationsSubTab('history');

      if (onShowToast) {
        onShowToast(`€${finalDonationAmount} tutarındaki online bağışınız / para yatırma işleminiz başarıyla gerçekleşti! Teşekkür ederiz.`, 'success', 'Bağış İşlemi');
      }
    }, 1200);
  };

  // WebAuthn Biometric State
  const [isBiometricRegistered, setIsBiometricRegistered] = useState(true);
  const [isBiometricTesting, setIsBiometricTesting] = useState(false);

  const handleToggleBiometrics = async () => {
    if (!isBiometricRegistered) {
      setIsBiometricTesting(true);
      try {
        if (typeof window !== 'undefined' && window.PublicKeyCredential) {
          const challenge = new Uint8Array(32);
          window.crypto.getRandomValues(challenge);
          const userId = new Uint8Array(16);
          window.crypto.getRandomValues(userId);

          await navigator.credentials.create({
            publicKey: {
              challenge,
              rp: { name: 'Kürt Kültür Derneği Yvelines' },
              user: {
                id: userId,
                name: user.email,
                displayName: user.fullName,
              },
              pubKeyCredParams: [{ alg: -7, type: 'public-key' }],
              timeout: 60000,
              attestation: 'direct',
            },
          }).catch((err) => console.log('Credential creation event:', err));
        }

        setTimeout(() => {
          setIsBiometricTesting(false);
          setIsBiometricRegistered(true);
          if (onShowToast) {
            onShowToast('Cihazınızın Face ID / Parmak İzi anahtarı (WebAuthn Passkey) başarıyla kaydedildi!', 'success', 'Biyometrik Güvenlik');
          }
        }, 1200);
      } catch (e) {
        setIsBiometricTesting(false);
      }
    } else {
      setIsBiometricRegistered(false);
      if (onShowToast) {
        onShowToast('Biyometrik giriş seçeneği pasife alındı.', 'info', 'Biyometrik Güvenlik');
      }
    }
  };

  const handleSaveProfile = (e: React.FormEvent) => {
    e.preventDefault();
    if (onShowToast) {
      onShowToast(t.profileSavedMsg || 'Profil bilgileriniz başarıyla güncellendi.', 'success', t.memberPanel);
    } else {
      alert(t.profileSavedMsg || 'Profil güncellendi!');
    }
  };

  // Dues state with sequential receipt numbers (only generated upon payment)
  const [duesList, setDuesList] = useState([
    { year: 2026, month: '01 - 06 / 2026 (1. Yarı Yıl)', amount: 120, status: 'paid', date: '2026-01-15', receiptNo: 'MKB-2026-0042', paymentMethod: 'Dernek Sanal POS Cihazı' },
    { year: 2026, month: '07 - 12 / 2026 (2. Yarı Yıl)', amount: 120, status: 'pending', date: '-', receiptNo: undefined, paymentMethod: '-' },
    { year: 2025, month: '2025 Yılı Dönemi', amount: 240, status: 'paid', date: '2025-01-10', receiptNo: 'MKB-2025-0811', paymentMethod: 'SEPA IBAN Transferi' },
  ]);

  // Printable Official Receipt Modal State
  const [selectedReceipt, setSelectedReceipt] = useState<{
    receiptNo: string;
    payerName: string;
    memberNo?: string;
    date: string;
    amount: number;
    currency: string;
    title: string;
    paymentMethod: string;
  } | null>(null);

  const handlePayDues = (idx: number) => {
    const newReceiptNo = 'MKB-2026-' + Math.floor(1000 + Math.random() * 9000);
    const updated = [...duesList];
    updated[idx].status = 'paid';
    updated[idx].date = new Date().toISOString().split('T')[0];
    updated[idx].receiptNo = newReceiptNo;
    updated[idx].paymentMethod = 'Dernek Sanal POS Cihazı';
    setDuesList(updated);

    // Auto open receipt for the newly paid dues
    setSelectedReceipt({
      receiptNo: newReceiptNo,
      payerName: user.fullName,
      memberNo: user.memberNo,
      date: updated[idx].date,
      amount: updated[idx].amount,
      currency: 'EUR',
      title: `${updated[idx].month} Dernek Üyelik Aidatı`,
      paymentMethod: 'Dernek Sanal POS Cihazı',
    });

    if (onShowToast) {
      onShowToast(`€${updated[idx].amount} aidat ödemeniz tamamlandı. Resmi makbuzunuz (${newReceiptNo}) oluşturuldu!`, 'success', 'Aidat Ödemesi');
    }
  };

  // Chat Simulation State
  const [chatMode, setChatMode] = useState<'membersLive' | 'board'>('membersLive');
  const [boardMessages, setBoardMessages] = useState([
    { id: '1', sender: 'Dernek Yönetimi', text: 'Silav u rêz! Dernek yönetim kuruluna hoş geldiniz.', time: '10:30' },
  ]);
  const [liveMemberMessages, setLiveMemberMessages] = useState([
    { id: '1', user: 'Zerya Dersim', text: 'Silav hevalno! Herkese iyi günler dilerim.', time: '18:45' },
    { id: '2', user: 'Amed Yılmaz', text: 'Yeni Newroz etkinliği programı çok güzel olmuş, emeği geçenlere teşekkürler!', time: '19:12' },
  ]);
  const [chatText, setChatText] = useState('');

  const handleSendChat = (e: React.FormEvent) => {
    e.preventDefault();
    if (!chatText.trim()) return;

    if (chatMode === 'board') {
      setBoardMessages([
        ...boardMessages,
        { id: Date.now().toString(), sender: user.fullName, text: chatText, time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) },
      ]);
    } else {
      setLiveMemberMessages([
        ...liveMemberMessages,
        { id: Date.now().toString(), user: user.fullName, text: chatText, time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) },
      ]);
    }
    setChatText('');
  };

  // 12 Main Modules Configuration for Grid Navigation
  const mainGridModules = [
    {
      id: 'profile',
      title: 'PROFİLİM & DİJİTAL KART',
      subtitle: '4 Alt Menü',
      icon: UserCheck,
      badge: 'Aktif Üye',
      badgeBg: 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 font-bold',
      subItems: [
        { id: 'card', title: 'DİJİTAL ÜYE KARTI (QR)', icon: QrCode, action: () => setProfileSubTab('card') },
        { id: 'edit', title: 'KİŞİSEL BİLGİLERİ DÜZENLE', icon: Edit, action: () => setProfileSubTab('edit') },
        { id: 'biometric', title: 'FACE ID / BİYOMETRİK KİLİT', icon: Fingerprint, action: () => setProfileSubTab('biometric') },
        { id: 'security', title: 'GÜVENLİK & ŞİFRE DEĞİŞTİR', icon: Lock, action: () => setProfileSubTab('security') },
      ],
    },
    {
      id: 'dues',
      title: 'AİDATLARIM & ÖDEMELER',
      subtitle: '4 Alt Menü',
      icon: CreditCard,
      badge: 'Ödeme Yap',
      badgeBg: 'bg-[#38BDF8]/20 text-[#38BDF8] border border-[#38BDF8]/40 font-bold',
      subItems: [
        { id: 'pending', title: 'AİDAT BORÇLARIM', icon: CreditCard, action: () => setDuesSubTab('pending') },
        { id: 'history', title: 'ÖDEME GEÇMİŞİM', icon: CheckCircle2, action: () => setDuesSubTab('history') },
        { id: 'receipt', title: 'AİDAT MAKBUZLARI', icon: Download, action: () => setDuesSubTab('receipt') },
        { id: 'auto_pay', title: 'OTOMATİK ÖDEME TALİMATI', icon: Sliders, action: () => setDuesSubTab('auto_pay') },
      ],
    },
    {
      id: 'events',
      title: 'ETKİNLİKLERİM & BİLETLERİM',
      subtitle: '4 Alt Menü',
      icon: Calendar,
      badge: '2 Bilet Aktif',
      badgeBg: 'bg-[#38BDF8]/20 text-[#38BDF8] border border-[#38BDF8]/40 font-bold',
      subItems: [
        { id: 'upcoming', title: 'KATILACAĞIM ETKİNLİKLER', icon: Calendar, action: () => setEventsSubTab('upcoming') },
        { id: 'tickets', title: 'QR BİLETLERİM', icon: QrCode, action: () => setEventsSubTab('tickets') },
        { id: 'history', title: 'GEÇMİŞ ETKİNLİKLER', icon: Clock, action: () => setEventsSubTab('history') },
        { id: 'calendar', title: 'ETKİNLİK TAKVİMİ', icon: Calendar, action: () => setEventsSubTab('calendar') },
      ],
    },
    {
      id: 'news',
      title: 'HABERLER & DUYURULAR',
      subtitle: '3 Alt Menü',
      icon: Newspaper,
      badge: 'Güncel',
      badgeBg: 'bg-[#38BDF8]/20 text-[#38BDF8] border border-[#38BDF8]/40 font-bold',
      subItems: [
        { id: 'announcements', title: 'SON DUYURULAR', icon: Bell, action: () => setNewsSubTab('announcements') },
        { id: 'all', title: 'DERNEK HABERLERİ', icon: Newspaper, action: () => setNewsSubTab('all') },
        { id: 'press', title: 'BASIN AÇIKLAMALARI', icon: FileText, action: () => setNewsSubTab('press') },
      ],
    },
    {
      id: 'activities',
      title: 'FAALİYETLERİM & PROJELER',
      subtitle: '3 Alt Menü',
      icon: Layers,
      badge: 'Faaliyetler',
      badgeBg: 'bg-[#38BDF8]/20 text-[#38BDF8] border border-[#38BDF8]/40 font-bold',
      subItems: [
        { id: 'list', title: 'DERNEK FAALİYETLERİ', icon: Layers, action: () => setActivitiesSubTab('list') },
        { id: 'reports', title: 'FAALİYET RAPORLARI', icon: FileText, action: () => setActivitiesSubTab('reports') },
        { id: 'docs', title: 'PROJE DOKÜMANLARI', icon: FolderPlus, action: () => setActivitiesSubTab('docs') },
      ],
    },
    {
      id: 'culture',
      title: 'KÜRT KÜLTÜRÜ & DİL DERSLERİ',
      subtitle: '4 Alt Menü',
      icon: BookOpen,
      badge: 'Dersler',
      badgeBg: 'bg-[#38BDF8]/20 text-[#38BDF8] border border-[#38BDF8]/40 font-bold',
      subItems: [
        { id: 'lessons', title: 'KÜRTÇE DİL DERSLERİ', icon: GraduationCap, action: () => setCultureSubTab('lessons') },
        { id: 'dictionary', title: 'KÜRTÇE SÖZLÜK', icon: Book, action: () => setCultureSubTab('dictionary') },
        { id: 'history', title: 'TARİH VE KÜLTÜR ARŞİVİ', icon: Landmark, action: () => setCultureSubTab('history') },
        { id: 'materials', title: 'DERS NOTLARI VE MATERYAL', icon: FileText, action: () => setCultureSubTab('materials') },
      ],
    },
    {
      id: 'donations',
      title: 'BAĞIŞ YAP (PARA YATIRMA)',
      subtitle: '4 Alt Menü',
      icon: HeartHandshake,
      badge: '❤️ BAĞIŞ YAP',
      badgeBg: 'bg-gradient-to-r from-red-600 to-rose-600 text-white border border-red-400 font-extrabold shadow-[0_0_12px_rgba(239,68,68,0.8)] animate-pulse',
      subItems: [
        { id: 'new', title: 'ONLINE BAĞIŞ YAP / PARA YATIR', icon: Plus, action: () => setDonationsSubTab('new') },
        { id: 'history', title: 'BAĞIŞ GEÇMİŞİM', icon: BarChart3, action: () => setDonationsSubTab('history') },
        { id: 'campaigns', title: 'DESTEK KAMPANYALARI', icon: Target, action: () => setDonationsSubTab('campaigns') },
        { id: 'receipts', title: 'BAĞIŞ MAKBUZLARIM', icon: Receipt, action: () => setDonationsSubTab('receipts') },
      ],
    },
    {
      id: 'gallery',
      title: 'GALERİ & MEDYA',
      subtitle: '3 Alt Menü',
      icon: ImageIcon,
      badge: 'Medya',
      badgeBg: 'bg-[#38BDF8]/20 text-[#38BDF8] border border-[#38BDF8]/40 font-bold',
      subItems: [
        { id: 'photos', title: 'FOTOĞRAF ALBÜMLERİ', icon: ImageIcon, action: () => setGallerySubTab('photos') },
        { id: 'videos', title: 'ETKİNLİK VİDEOLARI', icon: Video, action: () => setGallerySubTab('videos') },
        { id: 'archive', title: 'MEDYA ARŞİVİ', icon: Archive, action: () => setGallerySubTab('archive') },
      ],
    },
    {
      id: 'documents',
      title: 'BELGELER & FORMLAR',
      subtitle: '4 Alt Menü',
      icon: FileText,
      badge: 'Evraklar',
      badgeBg: 'bg-[#38BDF8]/20 text-[#38BDF8] border border-[#38BDF8]/40 font-bold',
      subItems: [
        { id: 'membership_cert', title: 'ÜYELİK BELGEM (PDF)', icon: FileCheck, action: () => setDocumentsSubTab('membership_cert') },
        { id: 'charter', title: 'DERNEK TÜZÜĞÜ', icon: BookOpen, action: () => setDocumentsSubTab('charter') },
        { id: 'forms', title: 'BAŞVURU FORMLARI', icon: FileEdit, action: () => setDocumentsSubTab('forms') },
        { id: 'downloads', title: 'İNDİRİLEBİLİR DOSYALAR', icon: Download, action: () => setDocumentsSubTab('downloads') },
      ],
    },
    {
      id: 'chat',
      title: 'ÜYE SOHBET & MESAJ',
      subtitle: '3 Alt Menü',
      icon: MessageSquare,
      badge: 'Canlı Chat',
      badgeBg: 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 font-bold',
      subItems: [
        { id: 'live', title: 'ÜYELER CANLI SOHBETİ', icon: Radio, action: () => setChatSubTab('live') },
        { id: 'board', title: 'YÖNETİM KURULU İLE İLETİŞİM', icon: ShieldCheck, action: () => setChatSubTab('board') },
        { id: 'notifications', title: 'BİLDİRİMLERİM (2)', icon: Bell, action: () => setChatSubTab('notifications') },
      ],
    },
    {
      id: 'live',
      title: 'CANLI YAYIN',
      subtitle: '2 Alt Menü',
      icon: Tv,
      badge: 'ŞİMDİ CANLI',
      badgeBg: 'bg-red-600 text-white font-bold border border-red-400 animate-pulse',
      subItems: [
        { id: 'watch', title: 'CANLI YAYINI İZLE', icon: Tv, action: () => setLiveSubTab('watch') },
        { id: 'archive', title: 'GEÇMİŞ YAYIN KAYITLARI', icon: Video, action: () => setLiveSubTab('archive') },
      ],
    },
    {
      id: 'contact',
      title: 'İLETİŞİM & DESTEK',
      subtitle: '3 Alt Menü',
      icon: PhoneCall,
      badge: '7/24',
      badgeBg: 'bg-[#38BDF8]/20 text-[#38BDF8] border border-[#38BDF8]/40 font-bold',
      subItems: [
        { id: 'info', title: 'DERNEK İLETİŞİM BİLGİLERİ', icon: PhoneCall, action: () => setContactSubTab('info') },
        { id: 'whatsapp', title: 'WHATSAPP DESTEK HATTI', icon: MessageSquare, action: () => setContactSubTab('whatsapp') },
        { id: 'map', title: 'KONUM & HARİTA', icon: MapPin, action: () => setContactSubTab('map') },
      ],
    },
  ];

  return (
    <div className="space-y-6 pb-16 w-full max-w-full overflow-x-hidden">
      {/* HEADER PROFILE CARD SUMMARY */}
      <div className="glass-card rounded-3xl p-6 sm:p-8 border border-[#D4AF37]/40 bg-gradient-to-r from-[#10241F] via-[#0B3D2E]/80 to-[#081B16] flex flex-col md:flex-row items-center justify-between gap-6 shadow-2xl">
        <div className="flex items-center gap-5">
          <img
            src={user.avatar}
            alt={user.fullName}
            className="w-20 h-20 rounded-2xl object-cover border-2 border-[#D4AF37] shadow-xl"
          />
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="text-2xl font-bold text-white font-heading">{user.fullName}</h1>
              <span className="px-3 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 text-[10px] font-extrabold uppercase tracking-wide">
                Aktif Üye
              </span>
            </div>
            <p className="text-xs text-[#D4AF37] font-bold mt-1">
              Üye No: {user.memberNo} • Kayıt: {user.joinDate}
            </p>
            <p className="text-xs text-gray-300 mt-1">
              📍 {user.city}, {user.country} • ✉️ {user.email}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              setActiveTab('dashboard');
              setViewLevel('main_grid');
            }}
            className="px-4 py-2 rounded-xl bg-[#132238] border border-[#38BDF8]/50 text-[#38BDF8] hover:bg-[#38BDF8] hover:text-[#0B1426] transition-all font-bold text-xs shadow-md flex items-center gap-2 cursor-pointer active:scale-95"
          >
            <LayoutDashboard className="w-4 h-4" />
            <span>Üye Paneli Ana Sayfası</span>
          </button>

          <button
            onClick={onLogout}
            className="btn-danger text-xs py-2 px-4 h-auto flex items-center gap-1.5 cursor-pointer"
          >
            <LogOut className="w-4 h-4" />
            <span>{t.logout || 'Çıkış Yap'}</span>
          </button>
        </div>
      </div>

      {/* ========================================================= */}
      {/* GRID BASED NAVIGATION SYSTEM FOR MEMBER PANEL */}
      {/* ========================================================= */}

      {/* 1. TOP BREADCRUMB & LEVEL BACK NAVIGATION BAR */}
      {(viewLevel === 'sub_grid' || viewLevel === 'action' || activeTab !== 'dashboard') && (
        <div className="bg-[#0B1426] p-3.5 rounded-2xl border border-[#38BDF8]/40 shadow-xl flex flex-wrap items-center justify-between gap-3 animate-in fade-in duration-200">
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                if (viewLevel === 'action') {
                  setViewLevel('sub_grid');
                } else {
                  setActiveTab('dashboard');
                  setViewLevel('main_grid');
                }
              }}
              className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-[#132238] border border-[#38BDF8]/50 text-[#38BDF8] hover:bg-[#38BDF8] hover:text-[#0B1426] transition-all font-bold text-xs shadow-md cursor-pointer active:scale-95"
            >
              <ChevronLeft className="w-4 h-4" />
              <span>{viewLevel === 'action' ? 'Modül Menüsüne Dön' : 'Üye Paneli Ana Sayfasına Dön'}</span>
            </button>

            <button
              onClick={() => {
                setActiveTab('dashboard');
                setViewLevel('main_grid');
              }}
              className="p-2 rounded-xl bg-[#132238] border border-[#38BDF8]/40 text-[#38BDF8] hover:bg-[#38BDF8] hover:text-[#0B1426] transition-all cursor-pointer"
              title="Üye Paneli Ana Sayfası"
            >
              <Home className="w-4 h-4" />
            </button>
          </div>

          <div className="flex items-center gap-2 text-xs font-bold text-[#7DD3FC]">
            <span className="opacity-60">Üye Paneli</span>
            <ChevronRight className="w-3.5 h-3.5 text-gray-500" />
            <span className="uppercase text-[#38BDF8]">
              {mainGridModules.find((m) => m.id === activeTab)?.title || 'ÜYE GÖSTERGE PANELİ'}
            </span>
          </div>
        </div>
      )}

      {/* 2. LEVEL 1: MAIN MODULES GRID */}
      {(activeTab === 'dashboard' || viewLevel === 'main_grid') && (
        <div className="space-y-4 animate-in fade-in duration-300">
          <div className="flex items-center justify-between px-1">
            <h2 className="text-sm font-black text-[#7DD3FC] uppercase tracking-wider flex items-center gap-2">
              <LayoutDashboard className="w-4 h-4 text-[#38BDF8]" />
              <span>Üye Paneli Modülleri</span>
            </h2>
            <span className="text-[11px] font-bold px-2.5 py-1 rounded-lg bg-[#38BDF8]/20 text-[#38BDF8] border border-[#38BDF8]/30">
              12 Modül Aktif
            </span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {mainGridModules.map((mod) => {
              const IconComp = mod.icon;

              return (
                <button
                  key={mod.id}
                  onClick={() => {
                    setActiveTab(mod.id);
                    setViewLevel('sub_grid');
                  }}
                  className="bg-[#0B1426] border border-[#38BDF8]/40 hover:border-[#38BDF8] rounded-2xl p-4 sm:p-5 flex flex-col justify-between hover:shadow-[0_0_25px_rgba(56,189,248,0.35)] transition-all cursor-pointer group text-left active:scale-95 min-h-[115px] sm:min-h-[130px] relative overflow-hidden"
                >
                  <div className="flex items-center justify-between w-full">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-[#132238] border border-[#38BDF8]/40 flex items-center justify-center text-[#38BDF8] group-hover:scale-110 group-hover:bg-[#38BDF8] group-hover:text-[#0B1426] transition-all shadow-md">
                      <IconComp className="w-5 h-5 stroke-[2.2]" />
                    </div>

                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold tracking-wide ${mod.badgeBg}`}>
                      {mod.badge}
                    </span>
                  </div>

                  <div className="mt-3">
                    <span className="text-xs sm:text-sm font-black text-white uppercase tracking-wider block group-hover:text-[#38BDF8] transition-colors line-clamp-1">
                      {mod.title}
                    </span>
                    <span className="text-[10px] text-gray-400 font-medium block mt-0.5">
                      {mod.subtitle}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* 3. LEVEL 2: SUB-MENU GRID */}
      {activeTab !== 'dashboard' && viewLevel === 'sub_grid' && (
        <div className="space-y-4 animate-in fade-in duration-300">
          {(() => {
            const currentMod = mainGridModules.find((m) => m.id === activeTab);
            if (!currentMod) return null;

            const IconMod = currentMod.icon;

            return (
              <>
                <div className="flex items-center justify-between px-1">
                  <h2 className="text-sm font-black text-[#7DD3FC] uppercase tracking-wider flex items-center gap-2">
                    <IconMod className="w-4 h-4 text-[#38BDF8]" />
                    <span>{currentMod.title} - İşlem Menüsü</span>
                  </h2>
                  <span className="text-[11px] font-bold px-2.5 py-1 rounded-lg bg-[#38BDF8]/20 text-[#38BDF8] border border-[#38BDF8]/30">
                    {currentMod.subItems.length} Seçenek
                  </span>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                  {currentMod.subItems.map((sub) => {
                    const SubIcon = sub.icon;

                    return (
                      <button
                        key={sub.id}
                        onClick={() => {
                          sub.action();
                          setViewLevel('action');
                        }}
                        className="bg-[#0B1426] border border-[#38BDF8]/40 hover:border-[#38BDF8] rounded-2xl p-4 sm:p-5 flex flex-col justify-between hover:shadow-[0_0_25px_rgba(56,189,248,0.35)] transition-all cursor-pointer group text-left active:scale-95 min-h-[110px] sm:min-h-[125px] relative"
                      >
                        <div className="flex items-center justify-between w-full">
                          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-[#132238] border border-[#38BDF8]/40 flex items-center justify-center text-[#38BDF8] group-hover:scale-110 group-hover:bg-[#38BDF8] group-hover:text-[#0B1426] transition-all shadow-md">
                            <SubIcon className="w-5 h-5 stroke-[2.2]" />
                          </div>
                          <ChevronRight className="w-4 h-4 text-gray-500 group-hover:text-[#38BDF8] group-hover:translate-x-1 transition-all" />
                        </div>

                        <div className="mt-3">
                          <span className="text-xs sm:text-sm font-black text-white uppercase tracking-wider block group-hover:text-[#38BDF8] transition-colors line-clamp-2">
                            {sub.title}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* ========================================================= */}
      {/* DETAILED ACTION VIEWS FOR MODULES */}
      {/* ========================================================= */}

      {/* 1. PROFİLİM & DİJİTAL KART */}
      {activeTab === 'profile' && (viewLevel === 'action' || viewLevel === 'sub_grid') && (
        <div className="space-y-6 animate-in fade-in duration-300">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
            {/* Digital Glassmorphic QR Member ID Card */}
            <div className="p-6 rounded-3xl bg-gradient-to-br from-[#0B3D2E] via-[#10241F] to-[#081B16] border-2 border-[#D4AF37] shadow-[0_0_30px_rgba(212,175,55,0.3)] space-y-6 relative overflow-hidden">
              <div className="flex items-center justify-between border-b border-[#D4AF37]/30 pb-4">
                <div>
                  <h3 className="text-xs font-extrabold text-[#D4AF37] uppercase tracking-widest">
                    RESMİ ÜYE DİJİTAL KARTI
                  </h3>
                  <p className="text-[10px] text-gray-300">Kürt Kültür Derneği Yvelines</p>
                </div>
                <ShieldCheck className="w-8 h-8 text-[#D4AF37]" />
              </div>

              <div className="flex items-center gap-4">
                <img
                  src={user.avatar}
                  alt={user.fullName}
                  className="w-20 h-20 rounded-2xl object-cover border-2 border-white/20"
                />
                <div className="space-y-1">
                  <p className="text-lg font-bold text-white font-heading">{user.fullName}</p>
                  <p className="text-xs text-[#D4AF37] font-mono">Üye No: {user.memberNo}</p>
                  <p className="text-[11px] text-emerald-400 font-semibold">✓ Aktif Üye Onaylı</p>
                </div>
              </div>

              <div className="p-4 rounded-2xl bg-black/40 border border-white/10 flex items-center justify-between">
                <div>
                  <p className="text-[10px] text-gray-400">QR BİLET & KİMLİK KODU</p>
                  <p className="text-xs font-mono text-white mt-0.5">VALID-2026-KKDD</p>
                </div>
                <div className="w-16 h-16 bg-white p-1 rounded-lg">
                  <svg viewBox="0 0 100 100" className="w-full h-full text-black" fill="currentColor">
                    <path d="M0,0 h30 v30 h-30 z M10,10 h10 v10 h-10 z M70,0 h30 v30 h-30 z M80,10 h10 v10 h-10 z M0,70 h30 v30 h-30 z M10,80 h10 v10 h-10 z M40,10 h20 v10 h-20 z M40,40 h20 v20 h-20 z M70,70 h10 v10 h-10 z M80,80 h20 v20 h-20 z" />
                  </svg>
                </div>
              </div>

              <button
                onClick={() => alert('Dijital Üye Kartınız indirildi!')}
                className="btn-primary w-full text-xs py-2.5 cursor-pointer"
              >
                <Download className="w-4 h-4" />
                <span>Dijital Kartı İndir (PDF)</span>
              </button>
            </div>

            {/* Profile Details Edit Form */}
            <form onSubmit={handleSaveProfile} className="glass-card rounded-3xl p-6 border border-white/10 space-y-4">
              <h3 className="text-lg font-bold text-white font-heading flex items-center gap-2">
                <Edit className="w-4 h-4 text-[#D4AF37]" />
                <span>Profil Bilgilerimi Düzenle</span>
              </h3>

              <div className="space-y-3 text-xs">
                <div>
                  <label className="text-gray-400 block mb-1">Telefon Numarası</label>
                  <input
                    type="text"
                    value={phoneVal}
                    onChange={(e) => setPhoneVal(e.target.value)}
                    className="w-full p-3 rounded-xl bg-[#081B16] border border-white/10 text-white"
                  />
                </div>

                <div>
                  <label className="text-gray-400 block mb-1">Adres Bilgisi</label>
                  <input
                    type="text"
                    value={addressVal}
                    onChange={(e) => setAddressVal(e.target.value)}
                    className="w-full p-3 rounded-xl bg-[#081B16] border border-white/10 text-white"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-gray-400 block mb-1">Şehir</label>
                    <input
                      type="text"
                      value={cityVal}
                      onChange={(e) => setCityVal(e.target.value)}
                      className="w-full p-3 rounded-xl bg-[#081B16] border border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-gray-400 block mb-1">Ülke</label>
                    <input
                      type="text"
                      value={countryVal}
                      onChange={(e) => setCountryVal(e.target.value)}
                      className="w-full p-3 rounded-xl bg-[#081B16] border border-white/10 text-white"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="btn-primary w-full py-2.5 text-xs cursor-pointer"
                >
                  <span>{t.saveChanges || 'Kaydet ve Güncelle'}</span>
                </button>
              </div>

              {/* WEBAUTHN BIOMETRIC SECURITY MANAGEMENT CARD */}
              <div className="pt-4 border-t border-white/10 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-extrabold text-white text-xs flex items-center gap-2">
                    <Fingerprint className="w-4 h-4 text-[#D4AF37]" />
                    <span>Biyometrik Kimlik Doğrulama (Face ID / Touch ID)</span>
                  </span>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${
                    isBiometricRegistered
                      ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
                      : 'bg-gray-500/20 text-gray-400 border-gray-500/40'
                  }`}>
                    {isBiometricRegistered ? '✓ Etkinleştirildi' : 'Devre Dışı'}
                  </span>
                </div>

                <div className="p-3.5 rounded-2xl bg-[#081B16] border border-white/10 space-y-2 text-[11px]">
                  <p className="text-gray-300 leading-relaxed">
                    Cihazınızın WebAuthn / FIDO2 donanım kilidi ile şifresiz Face ID / Parmak İzi güvenliği aktif.
                  </p>

                  <div className="flex items-center justify-between text-gray-400 pt-1 font-mono text-[10px]">
                    <span>Protokol: WebAuthn Level 3</span>
                    <span className="text-[#D4AF37]">FIDO2 / Passkey</span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleToggleBiometrics}
                  disabled={isBiometricTesting}
                  className={`w-full py-2.5 px-4 rounded-xl font-bold text-xs flex items-center justify-center gap-2 transition-all cursor-pointer ${
                    isBiometricRegistered
                      ? 'bg-red-500/10 text-red-300 border border-red-500/30 hover:bg-red-500/20'
                      : 'bg-[#0B3D2E] text-[#D4AF37] border border-[#D4AF37] hover:bg-[#10241F]'
                  }`}
                >
                  {isBiometricTesting ? (
                    <>
                      <ScanFace className="w-4 h-4 animate-spin text-[#D4AF37]" />
                      <span>Biyometrik Sensör Doğrulanıyor...</span>
                    </>
                  ) : (
                    <>
                      <Fingerprint className="w-4 h-4" />
                      <span>{isBiometricRegistered ? 'Biyometrik Girişi Kaldır' : 'Face ID / Parmak İzi Tanımla (WebAuthn Passkey)'}</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 2. AİDATLARIM & ÖDEMELER */}
      {activeTab === 'dues' && (viewLevel === 'action' || viewLevel === 'sub_grid') && (
        <div className="glass-card rounded-3xl p-6 border border-white/10 space-y-4 animate-in fade-in duration-300">
          <h3 className="text-lg font-bold text-white font-heading flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-[#D4AF37]" />
            <span>{t.myDues || 'Aidat Borçlarım & Ödeme Geçmişi'}</span>
          </h3>

          <div className="space-y-3">
            {duesList.map((item, idx) => (
              <div
                key={idx}
                className={`p-4 rounded-2xl border transition-all flex flex-col sm:flex-row items-center justify-between gap-4 ${
                  item.status === 'paid'
                    ? 'bg-emerald-500/5 border-emerald-500/20'
                    : 'bg-red-500/10 border-red-500/40'
                }`}
              >
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-white text-sm">{item.month}</span>
                    <span
                      className={`px-2 py-0.5 rounded text-[10px] font-extrabold uppercase tracking-wide ${
                        item.status === 'paid'
                          ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40'
                          : 'bg-red-500/20 text-red-400 border border-red-500/40'
                      }`}
                    >
                      {item.status === 'paid' ? 'ÖDENDİ' : '🔴 ÖDENMEDİ / BORÇLU'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-300 mt-1">
                    Tutar: <strong className="text-[#D4AF37]">€{item.amount}</strong> • Ödeme Tarihi: {item.date}
                    {item.receiptNo && (
                      <span className="ml-2 font-mono text-[10px] text-emerald-400">
                        (#Makbuz: {item.receiptNo})
                      </span>
                    )}
                  </p>
                </div>

                {item.status === 'pending' ? (
                  <button
                    onClick={() => handlePayDues(idx)}
                    className="btn-primary text-xs py-2 px-4 cursor-pointer shadow-lg active:scale-95"
                  >
                    <CreditCard className="w-4 h-4" />
                    <span>Şimdi Öde (€{item.amount})</span>
                  </button>
                ) : (
                  <button
                    onClick={() =>
                      setSelectedReceipt({
                        receiptNo: item.receiptNo || 'MKB-2026-0042',
                        payerName: user.fullName,
                        memberNo: user.memberNo,
                        date: item.date,
                        amount: item.amount,
                        currency: 'EUR',
                        title: `${item.month} Dernek Üyelik Aidatı`,
                        paymentMethod: item.paymentMethod || 'Dernek Sanal POS Cihazı',
                      })
                    }
                    className="btn-secondary text-xs py-2 px-4 cursor-pointer hover:border-[#D4AF37] flex items-center gap-1.5"
                  >
                    <Receipt className="w-4 h-4 text-[#D4AF37]" />
                    <span>Makbuz Görüntüle / İndir</span>
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 3. ETKİNLİKLERİM & BİLETLERİM */}
      {activeTab === 'events' && (viewLevel === 'action' || viewLevel === 'sub_grid') && (
        <div className="glass-card rounded-3xl p-6 border border-white/10 space-y-4 animate-in fade-in duration-300">
          <h3 className="text-lg font-bold text-white font-heading flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#D4AF37]" />
            <span>Etkinliklerim & QR Biletlerim</span>
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {events.slice(0, 4).map((evt) => (
              <div key={evt.id} className="p-4 rounded-2xl bg-white/5 border border-white/10 space-y-3">
                <h4 className="font-bold text-white text-sm">{evt.title[currentLang]}</h4>
                <p className="text-xs text-gray-400">📅 {evt.date} • 📍 {evt.location}</p>
                <div className="p-2.5 rounded-xl bg-[#0B3D2E] text-[#D4AF37] text-xs font-mono flex items-center justify-between">
                  <span>QR PASS BİLETİ AKTİF</span>
                  <QrCode className="w-4 h-4" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 4. HABERLER & DUYURULAR */}
      {activeTab === 'news' && (viewLevel === 'action' || viewLevel === 'sub_grid') && (
        <div className="glass-card rounded-3xl p-6 border border-white/10 space-y-4 animate-in fade-in duration-300">
          <h3 className="text-lg font-bold text-white font-heading flex items-center gap-2">
            <Newspaper className="w-5 h-5 text-[#D4AF37]" />
            <span>Dernek Haberleri & Duyurular</span>
          </h3>

          <div className="space-y-3">
            <div className="p-4 rounded-2xl bg-white/5 border border-white/10 space-y-2">
              <span className="px-2.5 py-0.5 rounded bg-[#38BDF8]/20 text-[#38BDF8] text-[10px] font-bold">Resmi Duyuru</span>
              <h4 className="font-bold text-white text-sm">Newroz 2026 Kutlama Programı Detayları Açıklandı</h4>
              <p className="text-xs text-gray-300">Bu yıl düzenlenecek büyük Newroz etkinliğimiz hakkında tüm detaylar ve sanatçı listesi belirlenmiştir.</p>
            </div>
            <div className="p-4 rounded-2xl bg-white/5 border border-white/10 space-y-2">
              <span className="px-2.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 text-[10px] font-bold">Kültür & Dil</span>
              <h4 className="font-bold text-white text-sm">Bahar Dönemi Kürtçe Dil Kursları Kayıtları Başladı</h4>
              <p className="text-xs text-gray-300">Kurmancî ve Zazakî lewel 1-2 sınıf ders kayıtlarımız tüm üyelerimize ücretsiz olarak açılmıştır.</p>
            </div>
          </div>
        </div>
      )}

      {/* 5. FAALİYETLERİM & PROJELER */}
      {activeTab === 'activities' && (viewLevel === 'action' || viewLevel === 'sub_grid') && (
        <div className="glass-card rounded-3xl p-6 border border-white/10 space-y-4 animate-in fade-in duration-300">
          <h3 className="text-lg font-bold text-white font-heading flex items-center gap-2">
            <Layers className="w-5 h-5 text-[#D4AF37]" />
            <span>Dernek Faaliyetleri & Proje Belgeleri</span>
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="p-4 rounded-2xl bg-white/5 border border-white/10 space-y-2">
              <h4 className="font-bold text-white text-sm">Gençlik Spor ve Kültür Turnuvası 2026</h4>
              <p className="text-xs text-gray-400">Yvelines bölgesindeki gençlerin kaynaşması adına düzenlenen geleneksel turnuva.</p>
              <span className="inline-block px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 text-[10px] font-bold">Tamamlandı</span>
            </div>
            <div className="p-4 rounded-2xl bg-white/5 border border-white/10 space-y-2">
              <h4 className="font-bold text-white text-sm">Kültür Kütüphanesi Arşivleme Projesi</h4>
              <p className="text-xs text-gray-400">Tarihi kitap ve dokümanların dijital ortama aktarılması faaliyeti.</p>
              <span className="inline-block px-2 py-0.5 rounded bg-[#38BDF8]/20 text-[#38BDF8] text-[10px] font-bold">Devam Ediyor</span>
            </div>
          </div>
        </div>
      )}

      {/* 6. KÜRT KÜLTÜRÜ & DİL DERSLERİ */}
      {activeTab === 'culture' && (viewLevel === 'action' || viewLevel === 'sub_grid') && (
        <div className="glass-card rounded-3xl p-6 border border-white/10 space-y-4 animate-in fade-in duration-300">
          <h3 className="text-lg font-bold text-white font-heading flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-[#D4AF37]" />
            <span>Kürtçe Dil Dersleri & Sözlük Materyalleri</span>
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="p-4 rounded-2xl bg-[#0B3D2E]/60 border border-[#D4AF37]/40 space-y-3">
              <div className="flex items-center justify-between">
                <span className="font-bold text-[#D4AF37] text-xs">🎓 Kurmancî Ders 1</span>
                <Play className="w-4 h-4 text-[#D4AF37]" />
              </div>
              <h4 className="font-extrabold text-white text-sm">Rêziman u Peyvên Bingehîn (Temel Gramer)</h4>
              <p className="text-xs text-gray-300">Sesli anlatım ve egzersiz çalışmaları içermektedir.</p>
              <button onClick={() => alert('Ders modülü başlatılıyor...')} className="btn-primary text-xs py-1.5 w-full cursor-pointer">Dersi Başlat</button>
            </div>

            <div className="p-4 rounded-2xl bg-[#0B3D2E]/60 border border-[#D4AF37]/40 space-y-3">
              <div className="flex items-center justify-between">
                <span className="font-bold text-[#D4AF37] text-xs">📖 Zazakî Ders 1</span>
                <Play className="w-4 h-4 text-[#D4AF37]" />
              </div>
              <h4 className="font-extrabold text-white text-sm">Zazakî Qal kerdene (Konuşma Pratiği)</h4>
              <p className="text-xs text-gray-300">Günlük konuşma kalıpları ve sözlük taraması.</p>
              <button onClick={() => alert('Ders modülü başlatılıyor...')} className="btn-primary text-xs py-1.5 w-full cursor-pointer">Dersi Başlat</button>
            </div>
          </div>
        </div>
      )}

      {/* 7. DESTEK OL & BAĞIŞ YAP */}
      {activeTab === 'donations' && (viewLevel === 'action' || viewLevel === 'sub_grid') && (
        <div className="glass-card rounded-3xl p-6 border border-white/10 space-y-6 animate-in fade-in duration-300">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-white/10 pb-4">
            <div>
              <h3 className="text-lg font-bold text-white font-heading flex items-center gap-2">
                <HeartHandshake className="w-5 h-5 text-[#D4AF37]" />
                <span>Bağış Geçmişim & Destek Kampanyaları</span>
              </h3>
            </div>
            <div className="px-4 py-2 rounded-2xl bg-[#0B3D2E] border border-[#D4AF37]/40 text-[#D4AF37] font-bold text-xs flex items-center gap-2 self-start sm:self-auto">
              <span>Toplam Destek:</span>
              <span className="text-white text-sm font-extrabold">
                €{donations.reduce((acc, curr) => acc + (curr.amount || 0), 0)}
              </span>
            </div>
          </div>

          <DonationHistoryTable donations={donations} currentLang={currentLang} />
        </div>
      )}

      {/* 8. GALERİ & MEDYA */}
      {activeTab === 'gallery' && (viewLevel === 'action' || viewLevel === 'sub_grid') && (
        <div className="glass-card rounded-3xl p-6 border border-white/10 space-y-4 animate-in fade-in duration-300">
          <h3 className="text-lg font-bold text-white font-heading flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-[#D4AF37]" />
            <span>Fotoğraf Albümleri & Etkinlik Videoları</span>
          </h3>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className="p-3 rounded-2xl bg-white/5 border border-white/10 text-center space-y-2">
              <div className="w-full h-24 rounded-xl bg-gray-800 flex items-center justify-center text-gray-500">📷 Foto 1</div>
              <p className="text-xs font-bold text-white">Newroz 2025 Albümü</p>
            </div>
            <div className="p-3 rounded-2xl bg-white/5 border border-white/10 text-center space-y-2">
              <div className="w-full h-24 rounded-xl bg-gray-800 flex items-center justify-center text-gray-500">📷 Foto 2</div>
              <p className="text-xs font-bold text-white">Kültür Gecesi 2025</p>
            </div>
            <div className="p-3 rounded-2xl bg-white/5 border border-white/10 text-center space-y-2">
              <div className="w-full h-24 rounded-xl bg-gray-800 flex items-center justify-center text-gray-500">🎥 Video</div>
              <p className="text-xs font-bold text-white">Konser Kayıtları</p>
            </div>
          </div>
        </div>
      )}

      {/* 9. BELGELER & FORMLAR */}
      {activeTab === 'documents' && (viewLevel === 'action' || viewLevel === 'sub_grid') && (
        <div className="glass-card rounded-3xl p-6 border border-white/10 space-y-4 animate-in fade-in duration-300">
          <h3 className="text-lg font-bold text-white font-heading flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#D4AF37]" />
            <span>Resmi Üyelik Belgeleri & Evraklar</span>
          </h3>

          <div className="space-y-3 text-xs text-gray-300">
            <div className="p-4 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-between">
              <div>
                <p className="font-bold text-white">Resmi Üyelik Onay Belgesi (PDF)</p>
                <p className="text-[10px] text-gray-400">Islağı İmzalı & QR Kod Onaylı • 1.2 MB</p>
              </div>
              <button onClick={() => alert('Üyelik Belgeniz indiriliyor...')} className="btn-primary py-1.5 text-xs cursor-pointer">
                Belgeyi İndir
              </button>
            </div>

            <div className="p-4 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-between">
              <div>
                <p className="font-bold text-white">Dernek Tüzüğü ve Yönetmelik</p>
                <p className="text-[10px] text-gray-400">Yvelines Dernek Tüzüğü • 2.4 MB</p>
              </div>
              <button onClick={() => alert('Tüzük belgesi indiriliyor...')} className="btn-primary py-1.5 text-xs cursor-pointer">
                Belgeyi İndir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 10. ÜYE SOHBET & MESAJ */}
      {activeTab === 'chat' && (viewLevel === 'action' || viewLevel === 'sub_grid') && (
        <div className="glass-card rounded-3xl p-6 sm:p-8 border border-white/10 space-y-6 animate-in fade-in duration-300">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-white/10 pb-4">
            <div>
              <h3 className="text-xl font-extrabold text-white font-heading flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-[#D4AF37]" />
                <span>Üye Canlı Sohbet Odası & Yönetim Mesajı</span>
              </h3>
              <p className="text-xs text-amber-200/80 mt-1 flex items-center gap-1.5">
                <Lock className="w-3.5 h-3.5 text-[#D4AF37]" />
                <span>Bu alan yalnızca dernek üyelerimizin erişimine açıktır.</span>
              </p>
            </div>

            {/* Chat Mode Toggle */}
            <div className="flex items-center gap-1.5 p-1 bg-[#081B16] rounded-2xl border border-white/10 self-start sm:self-auto">
              <button
                onClick={() => setChatMode('membersLive')}
                className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                  chatMode === 'membersLive'
                    ? 'bg-[#0B3D2E] text-[#D4AF37] border border-[#D4AF37]/40 shadow-md'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                <Radio className="w-3.5 h-3.5" />
                <span>Üye Sohbeti</span>
              </button>
              <button
                onClick={() => setChatMode('board')}
                className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                  chatMode === 'board'
                    ? 'bg-[#0B3D2E] text-[#D4AF37] border border-[#D4AF37]/40 shadow-md'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                <ShieldCheck className="w-3.5 h-3.5" />
                <span>Yönetim Kurulu</span>
              </button>
            </div>
          </div>

          {/* Chat Room Messages */}
          <div className="space-y-3 max-h-80 overflow-y-auto pr-2 bg-black/20 p-4 rounded-2xl border border-white/5">
            {chatMode === 'membersLive' ? (
              liveMemberMessages.map((m) => (
                <div
                  key={m.id}
                  className={`p-3.5 rounded-2xl text-xs ${
                    m.user.includes('Siz') || m.user === user.fullName
                      ? 'ml-auto max-w-lg bg-[#0B3D2E] text-white border border-[#D4AF37]/30'
                      : 'max-w-lg bg-white/10 text-gray-100 border border-white/5'
                  }`}
                >
                  <div className="flex items-center justify-between text-[10px] text-[#D4AF37] font-bold mb-1">
                    <span>{m.user}</span>
                    <span className="text-gray-400">{m.time}</span>
                  </div>
                  <p className="leading-relaxed">{m.text}</p>
                </div>
              ))
            ) : (
              boardMessages.map((m) => (
                <div
                  key={m.id}
                  className={`p-3.5 rounded-2xl text-xs ${
                    m.sender === user.fullName
                      ? 'ml-auto max-w-lg bg-[#0B3D2E] text-white border border-[#D4AF37]/30'
                      : 'max-w-lg bg-white/10 text-gray-100 border border-white/5'
                  }`}
                >
                  <div className="flex items-center justify-between text-[10px] text-[#D4AF37] font-bold mb-1">
                    <span>{m.sender}</span>
                    <span className="text-gray-400">{m.time}</span>
                  </div>
                  <p className="leading-relaxed">{m.text}</p>
                </div>
              ))
            )}
          </div>

          {/* Message Input Form */}
          <form onSubmit={handleSendChat} className="flex gap-2.5 pt-1">
            <input
              type="text"
              value={chatText}
              onChange={(e) => setChatText(e.target.value)}
              placeholder="Mesajınızı yazın..."
              className="flex-1 p-3.5 rounded-xl bg-[#081B16] border border-white/10 text-white text-xs focus:outline-none focus:border-[#D4AF37]"
            />
            <button type="submit" className="btn-primary px-5 py-3 text-xs font-bold cursor-pointer">
              <Send className="w-4 h-4" />
              <span>Gönder</span>
            </button>
          </form>
        </div>
      )}

      {/* 11. CANLI YAYIN */}
      {activeTab === 'live' && (viewLevel === 'action' || viewLevel === 'sub_grid') && (
        <div className="glass-card rounded-3xl p-6 border border-white/10 space-y-4 animate-in fade-in duration-300">
          <div className="flex items-center justify-between border-b border-white/10 pb-3">
            <h3 className="text-lg font-bold text-white font-heading flex items-center gap-2">
              <Tv className="w-5 h-5 text-red-500" />
              <span>Canlı Yayın Odası</span>
            </h3>
            <span className="px-3 py-1 rounded-full bg-red-600 text-white text-xs font-bold animate-pulse">
              ● CANLI YAYINDA
            </span>
          </div>

          <div className="aspect-video w-full rounded-2xl bg-black border border-red-500/30 flex flex-col items-center justify-center text-center p-6 space-y-3">
            <Tv className="w-12 h-12 text-red-500 animate-bounce" />
            <h4 className="font-extrabold text-white text-base">Kürt Kültür Derneği Canlı Yayını</h4>
            <p className="text-xs text-gray-400 max-w-md">Canlı yayınımız YouTube ve OBS bağlantısı ile doğrudan dernek üyelerimize aktarılmaktadır.</p>
          </div>
        </div>
      )}

      {/* 12. İLETİŞİM & DESTEK */}
      {activeTab === 'contact' && (viewLevel === 'action' || viewLevel === 'sub_grid') && (
        <div className="glass-card rounded-3xl p-6 border border-white/10 space-y-4 animate-in fade-in duration-300">
          <h3 className="text-lg font-bold text-white font-heading flex items-center gap-2">
            <PhoneCall className="w-5 h-5 text-[#D4AF37]" />
            <span>Dernek İletişim & WhatsApp Destek Hattı</span>
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
            <div className="p-4 rounded-2xl bg-white/5 border border-white/10 space-y-2">
              <p className="font-bold text-white text-sm">📍 Dernek Adresi</p>
              <p className="text-gray-300">Association Culturelle Kurde Yvelines, 78000 Versailles / France</p>
            </div>

            <div className="p-4 rounded-2xl bg-white/5 border border-white/10 space-y-2">
              <p className="font-bold text-white text-sm">📞 İletişim Numaraları</p>
              <p className="text-gray-300">Telefon: +33 1 34 56 78 90</p>
              <p className="text-gray-300">WhatsApp: +33 6 12 34 56 78</p>
            </div>
          </div>
        </div>
      )}

      {/* OFFICIAL PRINTABLE RECEIPT MODAL */}
      {selectedReceipt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md overflow-y-auto print:p-0 print:bg-white print:fixed print:inset-0">
          <div className="bg-[#0B1426] border-2 border-[#D4AF37] rounded-3xl p-6 sm:p-8 w-full max-w-2xl text-white space-y-6 shadow-[0_0_50px_rgba(212,175,55,0.3)] animate-in zoom-in-95 print:border-none print:shadow-none print:bg-white print:text-black">
            {/* Header */}
            <div className="flex items-start justify-between border-b border-[#D4AF37]/40 pb-5">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Receipt className="w-7 h-7 text-[#D4AF37]" />
                  <h2 className="text-lg font-black text-white uppercase tracking-wider font-heading print:text-black">
                    RESMİ TAHSİLAT MAKBUZU
                  </h2>
                </div>
                <p className="text-xs font-bold text-[#D4AF37] uppercase">
                  KÜRT KÜLTÜR VE DAYANIŞMA DERNEĞİ YVELINES
                </p>
                <p className="text-[10px] text-gray-300 print:text-gray-700">
                  ASSOC. CULTURELLE FRANCO-KURDE DE YVELINES • SIRET: 881 290 144 00018
                </p>
                <p className="text-[10px] text-gray-400 print:text-gray-600">
                  📍 250 bis boulevard maréchal juin 78200 Mantes La Jolie • 📞 +33 6 14 62 97 06
                </p>
              </div>

              <div className="text-right space-y-1">
                <span className="px-3 py-1 rounded-lg bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/40 font-mono text-xs font-extrabold block print:border-black print:text-black">
                  {selectedReceipt.receiptNo}
                </span>
                <p className="text-[10px] text-gray-300 print:text-gray-700 font-mono">
                  Tarih: {selectedReceipt.date}
                </p>
              </div>
            </div>

            {/* Receipt Content Table */}
            <div className="bg-[#10241F] border border-[#D4AF37]/30 rounded-2xl p-5 space-y-3 text-xs print:bg-gray-50 print:border-gray-300 print:text-black">
              <div className="grid grid-cols-2 gap-4 pb-3 border-b border-white/10 print:border-gray-300">
                <div>
                  <span className="text-gray-400 print:text-gray-600 block text-[10px] uppercase font-bold">ÖDEYEN ÜYE / KİŞİ</span>
                  <span className="font-extrabold text-white text-sm block mt-0.5 print:text-black">{selectedReceipt.payerName}</span>
                  {selectedReceipt.memberNo && (
                    <span className="text-[10px] font-mono text-[#D4AF37] block mt-0.5">Üye No: {selectedReceipt.memberNo}</span>
                  )}
                </div>
                <div>
                  <span className="text-gray-400 print:text-gray-600 block text-[10px] uppercase font-bold">ÖDEME YÖNTEMİ</span>
                  <span className="font-bold text-emerald-400 block mt-0.5 print:text-emerald-800">{selectedReceipt.paymentMethod}</span>
                  <span className="text-[10px] text-gray-400 block mt-0.5">Dernek Resmi Tahsilat Cihazı</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-1">
                <div>
                  <span className="text-gray-400 print:text-gray-600 block text-[10px] uppercase font-bold">TAHSİLAT NEDENİ / AÇIKLAMA</span>
                  <span className="font-bold text-white block mt-0.5 print:text-black">{selectedReceipt.title}</span>
                </div>
                <div>
                  <span className="text-gray-400 print:text-gray-600 block text-[10px] uppercase font-bold">TAHSİL EDİLEN TUTAR</span>
                  <span className="font-black text-xl text-[#D4AF37] block mt-0.5 print:text-black">
                    €{selectedReceipt.amount.toFixed(2)} {selectedReceipt.currency}
                  </span>
                </div>
              </div>
            </div>

            {/* Official Stamp & Verification Footer */}
            <div className="flex items-center justify-between pt-2 border-t border-white/10 print:border-gray-300">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white p-1 rounded-xl shrink-0">
                  <svg viewBox="0 0 100 100" className="w-full h-full text-black" fill="currentColor">
                    <path d="M0,0 h30 v30 h-30 z M10,10 h10 v10 h-10 z M70,0 h30 v30 h-30 z M80,10 h10 v10 h-10 z M0,70 h30 v30 h-30 z M10,80 h10 v10 h-10 z M40,10 h20 v10 h-20 z M40,40 h20 v20 h-20 z M70,70 h10 v10 h-10 z M80,80 h20 v20 h-20 z" />
                  </svg>
                </div>
                <div className="text-[10px] text-gray-400 print:text-gray-600">
                  <p className="font-bold text-emerald-400 print:text-emerald-800">✓ DİJİTAL KAŞELİ RESMİ MAKBUZ</p>
                  <p>İşbu makbuz dernek tüzüğü uyarınca tahsil edilmiş resmi belgedir.</p>
                </div>
              </div>

              <div className="text-right text-[10px] text-gray-400 print:text-gray-600">
                <p className="font-extrabold text-white print:text-black uppercase">Dernek Yönetim Kurulu Saymanlığı</p>
                <p className="italic mt-0.5">Kürt Kültür Derneği Yvelines</p>
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex items-center justify-end gap-3 pt-3 border-t border-white/10 print:hidden">
              <button
                onClick={() => window.print()}
                className="btn-primary text-xs py-2.5 px-5 flex items-center gap-2 cursor-pointer"
              >
                <Printer className="w-4 h-4" />
                <span>Makbuzu Yazdır / PDF İndir</span>
              </button>
              <button
                onClick={() => setSelectedReceipt(null)}
                className="btn-secondary text-xs py-2.5 px-4 cursor-pointer"
              >
                <span>Kapat</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
